import { globalMoveDataStore } from '../OverworldAnimation';
import type { Move } from './managers/MoveManager';
import MoveManager from './managers/MoveManager';

interface MoveDataLoaderStorage {
    store: Record<string, Move>;
}

export default class MoveDataLoader implements MoveDataLoaderStorage {
    store: Record<string, Move>;
    alwaysFetchFromAPI: boolean; // If true, always fetch from API and overwrite localStorage

    constructor() {
        this.store = {};
        this.alwaysFetchFromAPI = true; // Default to false (use cached data when available)
    }

    getMove(id: string): Move | undefined {
        return this.store[id];
    }

    async loadMoveData(): Promise<void> {
        try {
            // Check localStorage first if we're not forcing API fetch
            if (!this.alwaysFetchFromAPI) {
                const cachedData = this.loadFromLocalStorage();
                if (Object.keys(cachedData).length > 0) {
                    this.store = cachedData;
                    // Populate globalMoveDataStore from cached data
                    Object.keys(cachedData).forEach((moveID: string) => {
                        const move = cachedData[moveID];
                        (globalMoveDataStore as Record<string, Move>)[moveID] = move;
                    });
                    // Quick progress animation for localStorage load
                    const { animateQuickProgress } = await import('../OverworldAnimation.js');
                    await animateQuickProgress(
                        'MOVE_DATA',
                        `Loaded ${Object.keys(cachedData).length} moves from cache`
                    );
                    return;
                }
            }

            // Fetch from API or CSV if no cached data or alwaysFetchFromAPI is true
            const { updateLoadingProgress } = await import('../OverworldAnimation.js');
            const moveManager = new MoveManager();
            const source = moveManager.useAPI ? 'API' : 'CSV';
            updateLoadingProgress('MOVE_DATA', `Loading move data from ${source}...`);
            await moveManager.loadAllMoves();
            Object.keys(moveManager.store).forEach((moveID: string) => {
                const move = moveManager.getMove(moveID);
                if (move !== undefined) {
                    (globalMoveDataStore as Record<string, Move>)[moveID] = move;
                }
            });
            this.saveToLocalStorage(moveManager.store);
            this.store = moveManager.store;
        } catch (error) {
            const message = error instanceof Error ? error.message : String(error);
            console.error(`Error loading Move data: ${message}`);
        }
    }

    saveToLocalStorage(moveDatabase: Record<string, Move>): void {
        try {
            localStorage.setItem('move_data', JSON.stringify(moveDatabase));
            console.log('Move database saved to localStorage');
        } catch (error) {
            const message = error instanceof Error ? error.message : String(error);
            console.error(`Failed to save Move database to local storage: ${message}`);
        }
    }

    loadFromLocalStorage(): Record<string, Move> {
        try {
            const storedData = localStorage.getItem('move_data');
            if (storedData === null || storedData.trim() === '') {
                throw new Error('No move data in localStorage.');
            }
            return JSON.parse(storedData) as Record<string, Move>;
        } catch (error) {
            const message = error instanceof Error ? error.message : String(error);
            console.error(`Failed to load Move database from local storage: ${message}`);
            return {};
        }
    }
}

export const moveDataLoader = new MoveDataLoader();
